<template>
  <PageTemplate>
    <div class="element-wrapper">
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('audit.title') }}
        </h6>

        <CreateButton
            :label="$t('general.button.back')"
            :to="{ name: 'auditList', params:{officeId: office.id} }">
        </CreateButton>
      </div>

      <Loader v-if="!isLoaded"/>
      <div v-else>
        <form v-if="isLoaded" @submit.prevent="() => submitForm(false)" novalidate>
          <div class="element-box">
            <h5 class="border-bottom"><span class="me-2">Bezeichnung:</span> {{ office.name }}</h5>
            <h6 class="mb-5 mt-3"><span class="me-2">Extern/Intern:</span> {{ office.engagementTypeTranslation }}</h6>

            <div class="row">
              <BaseInput v-model="formData.officeId"
                         hidden="hidden"/>

              <div class="col-lg-6 col-md-12 col-sm-12">
                <div v-if="coordinatorDropdown && coordinatorDropdown.length">
                  <SelectVue
                      v-model="formData.coordinatorId"
                      :options="coordinatorDropdown"
                      :required="true"
                      :label="$t('general.coordinator')"
                      :placeholder="$t('placeholder.select_coordinator')"
                      :validation="v$.coordinatorId"
                      :messages="v$.coordinatorId.required.$message"
                  />
                </div>
              </div>
              <div class="col-lg-6 col-md-12 col-sm-12">
                <BaseInput v-model="formData.auditor"
                           :label="$t('general.auditor')"
                           :required="true"
                           :validation="v$.auditor"
                           :messages="v$.auditor.required.$message"/>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-2 col-md-4 col-sm-6 ">
                <Datepicker
                    v-model="formData.date"
                    :required="true"
                    :label="$t('general.date')"
                    position="down"
                    :validation="v$.date"
                    :messages="v$.date.required.$message"/>
              </div>
              <div class="col-lg-4 col-md-8 col-sm-12  ">
                <div class="row">
                  <div class="col-lg-6 col-md-12 col-sm-12">
                    <Timepicker
                        v-model="formData.startTime"
                        :required="true"
                        :minutesArray="minutesArray"
                        label="Startzeit"/>
                  </div>
                  <div class="col-lg-6 col-md-12 col-sm-12">
                    <Timepicker
                        v-model="formData.endTime"
                        :required="true"
                        :minutesArray="minutesArray"
                        label="Endzeit"/>
                  </div>
                </div>
              </div>
              <div class="col-lg-6 col-md-12 col-sm-12">
                <div v-if="participantsDropdown && participantsDropdown.length">
                  <SelectVue
                      v-model="formData.participantIds"
                      :options="participantsDropdown"
                      :is-multi="true"
                      :required="false"
                      :label="$t('general.participant')"/>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <TextEditor
                    v-model="formData.summary"
                    @update:model-value="formData.summary = $event"
                    :inputStyle="{ height: '160px' }"
                    :label="$t('general.summary')"/>
              </div>
            </div>
          </div>


          <div v-if="formData.previousConcernsAndSuggestions.length > 0" class="element-box">
            <div class="row">
              <h5>Bedenken und Vorschläge aus früheren Prüfungen</h5>
            </div>
            <div v-for="concern in formData.previousConcernsAndSuggestions" :key="concern.id">
              <div class="row">
                <div class="col-12 mt-3">
                  <TextEditor
                      v-model="concern.text"
                      @update:model-value="concern.text = $event"
                      :inputStyle="{ height: '160px' }"
                      :readOnly="true"
                      :label="$t('general.concerns_and_suggestions')"/>
                </div>

                <div class="col-12 mt-3 mb-2">
                  <TextEditor
                      v-model="concern.comment"
                      @update:model-value="concern.comment = $event"
                      :inputStyle="{ height: '160px' }"
                      :label="$t('general.add_comment')"/>
                </div>
              </div>
            </div>
          </div>

          <div class="element-box">
            <h5 class="mt-2">{{ $t('general.concerns_and_suggestions') }}</h5>
            <div v-for="(concernsAndSuggestion, index) in formData.concernsAndSuggestions" :key="index" class=" mb-2">
              <div class="row">
                <div class="col-12 mt-4">
                  <BaseInput v-model="concernsAndSuggestion.id"
                             hidden="hidden"/>

                  <TextEditor
                      v-model="concernsAndSuggestion.text"
                      @update:model-value="concernsAndSuggestion.text = $event"
                      :inputStyle="{ height: '160px' }"
                      :label="$t('general.concerns_and_suggestions')"/>

                  <BaseCheckbox
                      class="mt-2"
                      v-model="concernsAndSuggestion.includeInNext"
                      :labelPosition="'right'"
                      label="Als nächstes hinzufügen"/>

                  <button class="btn btn-danger mt-4"
                          @click.prevent="deleteConcernAndSuggestion(index)">
                    <i class="os-icon os-icon-trash"></i>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="auditStatus === 'planed'" class="form-buttons-w text-center mt-5">
              <div class="text-end mt-3">
                <button type="button" class="btn btn-primary" @click="addConcernAndSuggestion">+</button>
              </div>

              <SaveButton
                  :label="$t('general.button.save')"
                  :loading="isSubmitting"/>

              <button class="btn btn-primary" type="button"
                      value="save_and_complete"
                      @click.prevent="saveAndComplete">
                {{ $t('general.button.save_and_complete') }}
              </button>
            </div>
          </div>
        </form>
      </div>

      <ModalConfirm
          v-model="showSaveAndCompleteConfirm"
          confirmationQuestion="Bist du sicher?"
          :header="approveHeader"
          :onConfirm="() => submitForm(true)"/>
    </div>
  </PageTemplate>
</template>

<script setup>
import officeService from '@/api/Office';
import officeAuditService from '@/api/OfficeAudit';
import BaseCheckbox from '@/components/base/BaseCheckbox.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import CreateButton from '@/components/CreateButton.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import Loader from '@/components/Loader.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import SaveButton from '@/components/button/SaveButton.vue';
import SelectVue from '@/components/SelectVue.vue';
import TextEditor from '@/components/TextEditor.vue';
import Timepicker from '@/components/picker/Timepicker.vue';
import { ErrorToast, SuccessToast } from '@/components/VueToast';
import router from '@/router';
import { getCoordinatorForOfficeAuditDropdown, getUserForLocationDropdown } from '@/shared/dropdowns';
import { requiredRule } from '@/shared/validators/validationRules';
import useVuelidate from '@vuelidate/core';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { t }                      = useI18n();
const participantsDropdown       = ref([]);
const coordinatorDropdown        = ref([]);
const isLoaded                   = ref(false);
const isSubmitting               = ref(false);
const showSaveAndCompleteConfirm = ref(false);
const auditStatus                = ref('planed');
const office                     = ref({});
const route                      = useRoute();
const approveHeader              = ref('');
const officeId                   = route.params.officeId;
const auditId                    = route.params.id;

const formData = ref({
  officeId: '',
  coordinatorId: '',
  date: '',
  startTime: '00:00',
  endTime: '00:00',
  auditor: '',
  participantIds: [],
  summary: '',
  concernsAndSuggestions: [],
  previousConcernsAndSuggestions: [],
  saveAndComplete: false,
});

const rules = computed(() => {
  return {
    coordinatorId: requiredRule(t),
    date: requiredRule(t),
    auditor: requiredRule(t),
  };
});

const v$ = useVuelidate(rules, formData);

async function submitForm(saveAndComplete) {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    formData.value.officeId        = office.value.id;
    formData.value.saveAndComplete = saveAndComplete;

    if (auditId) {
      await officeAuditService.putOfficeAudit(formData.value, +auditId);
    } else {
      await officeAuditService.postOfficeAudit(formData.value);
    }

    await router.push({ name: 'auditList', params: { id: officeId } });
    SuccessToast(t('general.toasts.save_success'));
    isSubmitting.value = false;
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

function saveAndComplete() {
  showSaveAndCompleteConfirm.value = true;
}

async function addConcernAndSuggestion() {
  formData.value.concernsAndSuggestions.push({ id: '', text: '', includeInNext: false });
  v$.value.$touch();
}

async function deleteConcernAndSuggestion(index) {
  formData.value.concernsAndSuggestions.splice(index, 1);
}

async function getOffice() {
  try {
    const { data } = await officeService.getOffice(+officeId);

    office.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getAuditData(auditId) {
  try {
    const { data }                           = await officeAuditService.getOfficeAudit(+auditId);
    const participantsData                   = data.data?.participants;
    const concernAndSuggestionsData          = data.data?.concernAndSuggestions;
    const previousConcernAndSuggestionsArray = data.data?.previousAudit?.concernAndSuggestions;
    const coordinatorId                      = data.data?.coordinator.id;

    auditStatus.value = data.data?.status;

    const participantIds = [];
    participantsData.forEach(participantData => {
      const participant = {
        label: `${participantData.fullName}`,
        value: participantData.id,
      };

      participantIds.push(participant.value);

      const exists = participantsDropdown.value.some(item => item.value === participant.value);
      if (!exists) {
        participantsDropdown.value.push(participant);
      }
    });

    const concernAndSuggestionArray = concernAndSuggestionsData.map(item => ({
      id: item.id,
      text: item.text,
      includeInNext: item.includeInNext,
    }));

    formData.value = {
      ...data.data,
      coordinatorId: coordinatorId,
      participantIds: participantIds,
      concernsAndSuggestions: concernAndSuggestionArray,
      previousConcernsAndSuggestions: previousConcernAndSuggestionsArray,
    };

  } catch (error) {
    await ErrorToast(error);
  }
}

async function getPreviousConcernAndSuggestions() {
  try {
    const { data } = await officeAuditService.getOfficeAuditPreviousConcernAndSuggestions(+officeId);

    formData.value.previousConcernsAndSuggestions = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

const minutesArray = [
  '00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11',
  '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23',
  '24', '25', '26', '27', '28', '29', '30', '31', '32', '33', '34', '35',
  '36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47',
  '48', '49', '50', '51', '52', '53', '54', '55', '56', '57', '58', '59',
];

onMounted(async () => {
  await Promise.all([
    getUserForLocationDropdown(participantsDropdown, officeId),
    getCoordinatorForOfficeAuditDropdown(coordinatorDropdown, officeId),
    getOffice(),
  ]);

  if (auditId) {
    await getAuditData(auditId);
  } else {
    await getPreviousConcernAndSuggestions(officeId); // THIS IS IMPORTANT. MUST BE HERE
  }
  isLoaded.value = true;
});

</script>

<style scoped>
</style>